
import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import { get } from "lodash";
import "@/scripts/dictionary";
import { FeaturesList, PaginatedSearchQueryParams } from "@/store/types";
import NavHeader from "@/components/NavHeader.vue";
import GenericTable, { TableHeader } from "@/components/table/generic-table.vue";
import { ModalItem } from "@/components/table/generic-update-modal.vue";
import Numeric from "@/components/form/Numeric.vue";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import api from "@/services/api.service";
import { getTemplateNamesByEventNum } from "@/services/api.mobileRaiseTemplates.service";
import Script from "@/types/sv-data/events/Script";
import ResponseAlarmType from "@/types/sv-data/events/ResponseAlarmType";
import ServerTypeEventPagedResponse from "@/types/sv-data/events/ServerTypeEventPagedResponse";
import  { ServerTypeEventExtendedDto } from "@/types/sv-data/events/serverTypeEvent";
import { ServerTypeEventsPagedQueryParams } from "@/types/sv-data/events/ServerTypeEventsRequest";
import vSelect from "vue-select";
import { ServerTypeOption } from "@/types/sv-data/events/ServerTypeOption";
import { ServerType } from "@/types/sv-data/enums/ServerType";
import { YesNoOption } from "@/store/responses/types";
import { serviceLevelAgreementMixins } from '@/mixins';
import { helpers } from 'vuelidate/lib/validators';
import AlarmRaiseOption from "@/types/sv-data/enums/AlarmRaiseOptionTypes";
import { Constants, GenericTableConstants } from '@/types/Constants';
import { MaxLength } from '@/types/sv-data/event-configuration/Constants';
import { AlarmTag } from "@/store/eventqueue/types";
import { PaginatedSearchRequest } from '@/mixins/PaginatedSearch';
import AlarmTagsPagedResponse from "@/types/sv-data/alarms/AlarmTagsPagedResponse";
import organizationApi from "@/services/api.organizationSettings";
import { paginatedSearchExtendedMixins } from "@/mixins/paginatedSearchExtendedMixins";

const { fetchAllResults } = paginatedSearchExtendedMixins.methods;

const Areas = namespace("areas");
const GenericTableModule = namespace("GenericTable");

interface IEventGrouping {
    title: string,
    value: boolean | null,
}

interface IAlarmBehaviorOption {
	title: string,
	value: AlarmRaiseOption | null,
}

interface ServerTypeEventExtendedRow extends ServerTypeEventExtendedDto {
	serverTypeTitle: string | null;
	alarmBehaviorTitle: string | null;
}

const { validateSlaBreachSecondsValue, generateErrorMessage } = serviceLevelAgreementMixins.methods;

/**
 * Setup page for manual raise server type events
 */
@Component({
	components: {
		"nav-header": NavHeader,
		"generic-table": GenericTable,
		"vue-select": vSelect
	}
})
export default class EventConfigurationPage extends Vue{

	$refs!: {
		genericTable: GenericTable
	}

	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter("getPermissions") permissions: any;
	@Getter getUserTenantGroupId: number;
	@Getter getFeature: (featuresList: string[]) => boolean;
	@Getter("getMaxResponseSlaDuration") maxResponseSlaDuration: number;

	@GenericTableModule.Getter getModalRow: ServerTypeEventExtendedRow;

	private paginatedSearchQueryParams: PaginatedSearchQueryParams = null;
	private selectedServerType: ServerTypeOption = null;
	private	serverTypeId: number = ServerType.ManualRaise;
	private serverTypeEvents: ServerTypeEventExtendedDto[] = [];
	private alarmTags: AlarmTag[] = [];
	private responseAlarmTypes: ResponseAlarmType[] = [];
	private actionPlans: Script[] = [];
	private isLoading: boolean = false;
	private totalRecords: number = 0;
	private tenantServerTypes: ServerTypeOption[] = [];

	private mostRecentSearchParams: ServerTypeEventsPagedQueryParams = null;
	private selectedGroupId: number = 0;

    private readonly eventGroupingOptions: IEventGrouping[] =
	[
		{
			title: "Use Default",
			value: null,
		},
		{
			title: "Enabled",
			value: true,
		},
		{
			title: "Disabled",
			value: false
		}
	];

	private readonly yesNoNotSet: YesNoOption[] = [
        {
            title: "Not set",
            value: null
        },
		{
            title: "Yes",
            value: true
        },
        {
            title: "No",
            value: false
        }
    ];

	private get alarmBehaviorOptions(): IAlarmBehaviorOption[] {
		const options: IAlarmBehaviorOption[] = [
			{
				title: "Not Set",
				value: null
			},
			{
				title: "Default Ignore",
				value: AlarmRaiseOption.DefaultIgnore
			},
			{
				title: "Default Auto Handle",
				value: AlarmRaiseOption.DefaultAutoHandle
			}
		];

		if (this.eventTypeIgnoreWithStatusEnabled) {
			options.push({
				title: "Ignore With Status",
				value: AlarmRaiseOption.IgnoreWithStatus
			});
		}

		return options;
	}

	private get columns (): TableHeader[] {
		const columns : TableHeader[] = [
			{
				title: "Title",
				key: "title",
				order: 1,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Event title",
				searchable: true,
				visible: true,
				dataType: "input",
				isTermLabel: true,
				sortable: true,
				isSortedByDefault: true,
				defaultSortDirection: GenericTableConstants.SortDirectionAscending
			},
			{
				title: "Description",
				key: "description",
				order: 2,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Description",
				searchable: true,
				visible: true,
				dataType: "input",
				sortable: true,
			},
			{
				title: "Integration Type",
				key: "serverTypeTitle",
				order: 3,
				sortOrder: 0,
				sortOrderReversed: false,
				sortable: true,
				visible: true,
				disable: !this.tenantServerTypesEnabled,
				sortKey: "serverTypeID",
				description: "Type of Integration event originated from."
			},
			{
				title: "Area",
				key: "groupTitle",
				order: 4,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Area",
				searchable: false,
				visible: true,
				dataType: "input",
				sortable: true,
			},
			{
				title: "Default Priority",
				key: "defaultPriority",
				order: 5,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Priority to use unless overridden",
				searchable: false,
				visible: true,
				dataType: "input",
				sortable: true
			},
			{
				title: "Default Action Plan",
				key: "defaultScriptTitle",
				order: 6,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Action plan to use unless overridden",
				searchable: false,
				visible: true,
				dataType: "input",
				sortable: true,
			},
			{
				title: "Alarm Behavior",
				key: "alarmBehaviorTitle",
				description: "Should alarm by default be ignored, auto handled or ignored with status.",
				order: 7,
				searchable: false,
				visible: true,
				disable: !this.tenantServerTypesEnabled,
				sortable: true,
				sortOrder: 0,
				sortOrderReversed: false
			},
			{
				title: "Raise Alarms Serially",
				key: "isRaiseIndividual",
				description: "Do not group with other alarms, so every new alarm will be raised separately.",
				order: 10,
				searchable: false,
				visible: false,
				sortable: true,
				sortOrder: 0,
				sortOrderReversed: false,
				disable: !this.raiseAlarmSeriallyEnabled,
				useCustomCell: true
			},
			{
				title: "Event Code",
				key: "eventCode",
				order: 15,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Event Code",
				searchable: false,
				visible: false,
				disable: !this.tenantServerTypesEnabled,
				dataType: "input",
				isTermLabel: true,
				sortable: true,
			},
			{
				title: "Restore Code",
				key: "restoreCode",
				order: 16,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Restore Code",
				searchable: false,
				visible: false,
				disable: !this.tenantServerTypesEnabled,
				dataType: "input",
				isTermLabel: true,
				sortable: true,
			},
			{
				title: "Requires Restore",
				key: "restorable",
				order: 17,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Requires Restore",
				searchable: false,
				visible: false,
				disable: !this.tenantServerTypesEnabled,
				isTermLabel: true,
				sortable: true,
				useCustomCell: true,
			},
			{
				title: "Can Restore",
				key: "restored",
				order: 18,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Requires Restore",
				searchable: false,
				visible: false,
				disable: !this.tenantServerTypesEnabled,
				isTermLabel: true,
				sortable: true,
				useCustomCell: true,
			},
			{
				title: "Allow Extra Text",
				key: "allowExtraText",
				description: "Allow Extra Text to be used when raising an Alarm.",
				order: 19,
				searchable: false,
				visible: false,
				disable: !this.tenantServerTypesEnabled,
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				useCustomCell: true
			}
		];

		if(this.situationalAwarenessEnabled)
		{
			columns.push({
				title: "Shown in Situational Awareness",
				key: "showInSituationAwareness",
				order: 12,
				searchable: false,
				visible: false,
				sortable: true,
				sortOrder: 0,
				sortOrderReversed: false,
				useCustomCell: true
			});
		}

		if (this.geofenceEnabled)
		{
			columns.push({
				title: "Match GeoFence",
				key: "matchGeofence",
				order: 13,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Match geofence for recording",
				searchable: false,
				visible: true,
				dataType: "input",
				useCustomCell: true
			});
			columns.push({
				title: "GeoFence Radius",
				key: "matchRadius",
				order: 14,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Record within radius (in meters)",
				searchable: false,
				visible: true,
				dataType: "input"
			});
		}

		if (this.expectedAlarmFailureEnabled) {
			columns.push({
				title: "Default Expected Frequency (in minutes)",
				key: "defaultExpectedFrequencyInSeconds",
				order: 15,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Default Expected Frequency (in minutes)",
				searchable: false,
				visible: true,
			});
		}

		if (this.isAlarmTagsEnabled) {
			columns.push({
				title: "Default Alarm tag",
				key: "defaultTagTitle",
				description: "The tag associated with this Event Type",
				order: 9,
				visible: this.isAlarmTagsEnabled,
				searchable: false,
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false
			});
		}

		return columns;
	}

	private get modalItems (): ModalItem[] {
		return [
			{
				title: "Title",
				key: "title",
				dataType: "text",
				required: true,
				maxLength: 200
			},
			{
				title: "Description",
				key: "description",
				dataType: "text",
				maxLength: 100
			},
			{
				title: "Device Type",
				key: "serverTypeID",
				dataType: "vselect3",
				data: this.tenantServerTypes,
				props: {
					reduce: o => o.serverTypeId,
				},
				readOnlyIfEdit: true,
				visible: this.tenantServerTypesEnabled,
				defaultValue: this.serverTypeId
			},
			{
				title: "Area",
				key: "groupID",
				description: "Allow this event to be used here and any areas below",
				dataType: "component",
				readOnly: false,
				required: !this.permissions.isSystemAdmin,
				data: AreaTreeSelect,
				props: {
					reduce: g => g.id,
					requiredPermissions: ["CanEditSiteSetup"],
					clearable: this.permissions.isSystemAdmin
				},
				defaultValue: this.getUserTenantGroupId,
			},
			{
				title: "Default Priority",
				key: "defaultPriority",
				description: "Priority to use unless overridden, maximum 999999",
				dataType: "component",
				data: Numeric,
				max: 999999,
				min: 0,
				props: {
					max: 999999,
					min: 0
				}
			},
			{
				title: "Default Action Plan",
				key: "defaultScript",
				description: "Action plan to use unless overridden",
				dataType: "vselect3",
				data: this.actionPlans,
				props: {
					reduce: o => o.scriptID
				}
			},
			{
				title: "Alarm Behavior",
				key: "alarmBehavior",
				description: "Should alarm by default be ignored, auto handled or ignored with status.",
				dataType: "vselect3",
				data: this.alarmBehaviorOptions,
				props: {
					reduce: ar => ar.value
				},
				visible: this.tenantServerTypesEnabled,
				validations: {
						ignoreWithStatusDisabled: helpers.withParams(
						{
							type: "ignoreWithStatusDisabled",
							errorMessage: "IgnoreWithStatus feature is not enabled. Please choose a valid value from the dropdown."
						},
						(value) => {
							// IgnoreWithStatus feature is not enabled and alarm behavior is ignore with status and
							if (!this.eventTypeIgnoreWithStatusEnabled && value === AlarmRaiseOption.IgnoreWithStatus) {
								return false;
							}
							return true;
						}
					),
				},
			},
			{
				title: "Shown in Situational Awareness",
				key: "showInSituationAwareness",
				dataType: "checkbox",
				data: Boolean,
				defaultValue: false,
				visible: this.situationalAwarenessEnabled
			},
			{
				title: "Raise Alarms Serially",
				key: "isRaiseIndividual",
				dataType: "vselect3",
				visible: this.raiseAlarmSeriallyEnabled,
				description: "Raise every alarm of this type to its own event.",
				data: this.eventGroupingOptions,
				props: {
					reduce: options => options.value
				},
				defaultValue: true
			},
			{
				title: "Default Alarm Tag",
				key: "defaultTag",
				description: "The tag associated with this Event Type",
				dataType: "vselect3",
				data: this.alarmTags,
				props: {
					reduce: (tag: AlarmTag) => tag.responseAlarmTypeID,
					clearable: true,
					label: "title",
				},
				visible: this.isAlarmTagsEnabled,
				defaultValue: null,
			},
			{
				title: "Match Geofence",
				key: "matchGeofence",
				dataType: "checkbox",
				description: "Record nearby cameras when raised",
				visible: this.geofenceEnabled
			},
			{
				title: "Match Geofence Radius",
				description: "Record cameras within this many meters",
				key: "matchRadius",
				visible: this.geofenceEnabled,
				dataType: "component",
				data: Numeric,
				max: 9999,
				min: 0,
				props: {
					max: 9999,
					min: 0
				}
			},
			{
				title: "Event Code",
				key: "eventCode",
				dataType: "textarea",
				maxLength: MaxLength.EVENT_CODE,
				showMaxLength: true,
				visible: this.tenantServerTypesEnabled,
			},
			{
				title: "Restore Code",
				key: "restoreCode",
				dataType: "textarea",
				maxLength: MaxLength.RESTORE_CODE,
				showMaxLength: true,
				visible: this.tenantServerTypesEnabled,
			},
			{
				title: "Requires Restore",
				key: "restorable",
				data: Boolean,
				dataType: "checkbox",
				defaultValue: false,
				visible: this.tenantServerTypesEnabled,
			},
			{
				title: "Can Restore",
				key: "restored",
				data: Boolean,
				dataType: "checkbox",
				defaultValue: false,
				visible: this.tenantServerTypesEnabled,
			},
			{
				title: "Allow Extra Text",
				key: "allowExtraText",
				dataType: "vselect3",
				visible: this.tenantServerTypesEnabled,
				data: this.yesNoNotSet,
				props: {
					reduce: options => options.value
				},
				defaultValue: null
			},
			{
				title: "Service Level Agreement (SLA) in Seconds",
				key: "slaBreachSeconds",
				dataType: "number",
				readOnly: !(this.serviceLevelAgreementsEnabled && this.permissions.canEditSLAs),
				visible: this.serviceLevelAgreementsEnabled && (this.permissions.canViewSLAs || this.permissions.canEditSLAs),
				placeholder: 'Enter the number of seconds permitted before an alarm breaches the SLA.',
				defaultValue: null,
				min: 0,
				validations: {
					isSlaValueValid: helpers.withParams(
						{
							type: "isSlaValueValid",
							errorMessage: generateErrorMessage(this.maxResponseSlaDuration)
						},
						(value) => {
							return validateSlaBreachSecondsValue(value, this.maxResponseSlaDuration)
						}
					)
				},
				order: 11,
			},
			{
				title: "Default Expected Frequency (minutes)",
				key: "defaultExpectedFrequencyInSeconds",
				dataType: "number",
				readOnly: !(this.expectedAlarmFailureEnabled),
				visible: this.expectedAlarmFailureEnabled,
				placeholder: 'Enter the number of minutes for Expected Frequency of Timer Tests',
				defaultValue: null,
				min: 0,
				max: Constants.MAX_EXPECTED_FREQUENCY,
				order: 12,
			},
		] as ModalItem[]
	}

	private get tableItems (): ServerTypeEventExtendedRow[]
	{
		let mappedItems: ServerTypeEventExtendedRow[] = this.serverTypeEvents.map(type => {
			let newType: ServerTypeEventExtendedRow = Object.assign(type, {
            serverTypeTitle: this.tenantServerTypes.find(tst => tst.serverTypeId === type.serverTypeID)?.title,
			alarmBehaviorTitle: this.alarmBehaviorOptions.find(abo => abo.value === type.alarmBehavior)?.title
        });

			if (type.defaultExpectedFrequencyInSeconds != null) {
				type.defaultExpectedFrequencyInSeconds /= 60;
			}

			return newType;
		});
		return mappedItems;
	}

	private async mounted(): Promise<void> {
		if (this.isAlarmTagsEnabled) {
            await this.fetchAllAlarmTags();
        }
		if (!this.eventTypeManagementEnabled || !this.permissions.canEditSettings) {
			this.$router.go(-1);
		}

		if(this.tenantServerTypesEnabled)
		{
			this.tenantServerTypes = await api.getServerTypesForTenant();
			// there is a watch on selectedServerType which calls updateState.
			this.selectedServerType = this.tenantServerTypes.find(type => type.serverTypeId == ServerType.ManualRaise);
		}
		else
		{
			this.updateState();
		}
	}

	private async updateState(ServerTypeEventsPagedQueryParams?: ServerTypeEventsPagedQueryParams) {
		try {
			this.isLoading = true;
			if(!ServerTypeEventsPagedQueryParams){
				ServerTypeEventsPagedQueryParams = {
					page: 1,
					pageSize: 25,
					sortDesc: false,
					sortBy: "Title",
					searchTerm: ""
				}
			}
			this.mostRecentSearchParams = ServerTypeEventsPagedQueryParams;

			// Ensure were looking for manual raises
			ServerTypeEventsPagedQueryParams.serverTypeIds = [this.serverTypeId];

            let serverEventTypeResponse: ServerTypeEventPagedResponse = await api.pagedServerTypeEvents(ServerTypeEventsPagedQueryParams);

			this.totalRecords = serverEventTypeResponse.totalRecords

			this.serverTypeEvents = serverEventTypeResponse.data

			this.actionPlans = await api.loadActionPlans();
			this.responseAlarmTypes = await api.responseAlarmTypes();
		} catch (ex) {
			console.log("Unexpected error updating component state: " + ex);
		} finally {
			this.isLoading = false;
		}
	}

	private get raiseAlarmSeriallyEnabled(): boolean {
		return this.getFeature(["EventTypeSetup", "TenantServerTypes", "EditRaiseAlarmsSerially"]);
	}

	private get eventTypeManagementEnabled() {
		return get(this.featuresList, ["EventTypeSetup"]);
	}

	private get serviceLevelAgreementsEnabled(): boolean {
		return !!get(this.featuresList, ["Alarms", "SLAs"]);
	}

	private get tenantServerTypesEnabled(): boolean
	{
		return this.getFeature(["EventTypeSetup", "TenantServerTypes"]);
	}

	private get situationalAwarenessEnabled(): boolean
	{
		return this.getFeature(["SituationalAwareness"]);
	}

	private get geofenceEnabled(): boolean
	{
		return this.getFeature(["EventTypeSetup", "Geofence"]);
	}

	private get expectedAlarmFailureEnabled(): boolean {
		return this.getFeature(["Devices", "AdvancedSetup", "ExpectedAlarmFailures"]);
	}

	private get eventTypeIgnoreWithStatusEnabled(): boolean {
		return this.getFeature(["EventTypeSetup", "IgnoreWithStatus"]);
	}

	private async updateItem (updateEvent: ServerTypeEventExtendedRow): Promise<void> {
			if (updateEvent) {
			try {
				this.isLoading = true;
				// ensure that not null boolean are not null!
				updateEvent.ignoreDefaultAlarm = updateEvent.ignoreDefaultAlarm || false;
				updateEvent.serverTypeID = updateEvent.serverTypeID || this.serverTypeId;
				updateEvent.restorable = updateEvent.restorable || false;
				updateEvent.restored = updateEvent.restored || false;

				if (this.expectedAlarmFailureEnabled &&
					updateEvent.defaultExpectedFrequencyInSeconds != null &&
					updateEvent.defaultExpectedFrequencyInSeconds > 0) {
					updateEvent.defaultExpectedFrequencyInSeconds *= 60;
				}
				else
				{
					updateEvent.defaultExpectedFrequencyInSeconds = null;
				}

				await api.serverTypeEventUpdate(updateEvent);
				await this.updateState(this.mostRecentSearchParams);
			} catch (ex) {
				console.log("Unexpected error updating server type event: " + ex);
			} finally {
				this.isLoading = false;
			}
		}
	}

	private async deleteItem (deleteEvent: ServerTypeEventExtendedRow) {
		try {
			this.isLoading = true;
			await api.serverTypeEventDelete(deleteEvent);
			await this.updateState(this.mostRecentSearchParams);
		} catch (ex) {
			console.log("Unexpected error deleting server type event: " + ex);
		} finally {
			this.isLoading = false;
		}
	}

	private async deleteActionMessages(eventToDelete: ServerTypeEventExtendedRow): Promise<string[]> {
		const usedInTemplates = await getTemplateNamesByEventNum(eventToDelete.eventNum);
		if (usedInTemplates && usedInTemplates.length) {
			const msg = `Remove this Event from Mobile Raise Templates: ${usedInTemplates.join(", ")}`;
			return [msg];
		}
		return [];
	}

	private get isAlarmTagsEnabled(): boolean {
		return !!get(this.featuresList, ["Alarms", "AlarmTags"]);
	}

	private async fetchAllAlarmTags(): Promise<void> {
		const pageRequest: PaginatedSearchRequest = {
				params: { ...this.paginatedSearchQueryParams },
				cancelToken: undefined
			};

		if (this.getModalRow?.groupID) {
			this.selectedGroupId = this.getModalRow.groupID;
			pageRequest.params.groupId = this.getModalRow.groupID;
		}

		const results = await fetchAllResults<AlarmTagsPagedResponse>(organizationApi, organizationApi.retrieveAlarmTags, pageRequest);

		if (results.data.length > 0 && this.getModalRow?.defaultTag && !results.data.some(x => x.responseAlarmTypeID === this.getModalRow.defaultTag)) {
			let copy = {...this.getModalRow, defaultTag: null, defaultTagTitle: null}
			this.$refs?.genericTable?.updateNewEntry(copy, ["defaultTag"])
		}

        this.alarmTags = results.data;
	}

    @Watch("getModalRow", { deep:true })
	private async modalRowWatch(): Promise<void> {
		if (this.isAlarmTagsEnabled
			&& this.getModalRow.groupID !== 0
			&& this.getModalRow.groupID !== this.selectedGroupId) {
			await this.fetchAllAlarmTags();
		}
	}

	@Watch("selectedServerType")
	private onServerTypeChange(): void
	{
		this.serverTypeId = this.selectedServerType?.serverTypeId || ServerType.ManualRaise;
		this.updateState();
	}
}
